<template>
  <div
    ref="dateRequestSuccessRef"
    class="flex flex-col"
    style="scroll-margin-top: 100px"
  >
    <div class="complete-alert">
      <IconRocket class="w-[19px] h-[19px]" />
      <p>{{ $t('product.dateRequest.eventSuggestions.requestSent') }}</p>
    </div>

    <DateRequestProductList
      :private-request="isPrivate"
      data-test-id="date-request-similar-events"
      is-single-column
      :requested="true"
      :loading="similarEventsLoading"
      :products="eventsRequested"
    />

    <template v-if="eventsWithBookableDate.length > 0">
      <div class="font-medium my-3">
        {{
          $t('product.dateRequest.eventSuggestions.bookableExperiencesTitle')
        }}
      </div>

      <DateRequestProductList
        :private-request="isPrivate"
        data-test-id="date-request-bookable-events"
        is-single-column
        :loading="similarEventsLoading"
        :products="eventsWithBookableDate"
        @request-success="handleRequestSuccess"
      />
    </template>

    <template v-if="eventsWithoutBookableDate.length > 0">
      <div class="font-medium my-3">
        {{ $t('product.dateRequest.eventSuggestions.title') }}
      </div>

      <DateRequestProductList
        :private-request="isPrivate"
        data-test-id="date-request-similar-events"
        is-single-column
        :loading="similarEventsLoading"
        :products="eventsWithoutBookableDate"
        @request-success="handleRequestSuccess"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import DateRequestProductList from './DateRequestProductList.vue'
import IconRocket from '~/assets/icons/icon-rocket.svg?component'
import type { DateRequestSimilarEvent } from '~/composables/types/event'
import { useFetchDateRequestSimilarEvents } from '~/composables/product'

const { t: $t } = useI18n()

const { dateRequestId = undefined } = defineProps<{
  dateRequestId?: string
}>()

const requestedEventIds = ref<string[]>([])
const dateRequestSuccessRef = ref<HTMLElement | null>(null)
const isPrivate = computed(() => similarEventsResponse.value?.data?.is_private)
defineEmits<{
  onCloseDateRequest: []
}>()

const { data: similarEventsResponse, pending: similarEventsLoading } =
  useFetchDateRequestSimilarEvents(dateRequestId || '', {
    lazy: true,
    server: false,
    default: () =>
      ({
        data: { id: '', private: false, similar_events: { data: [] } },
      }) as any,
  })

const similarEvents = computed(
  () => similarEventsResponse.value?.data?.similar_events?.data || []
)

const handleRequestSuccess = (eventData: DateRequestSimilarEvent) => {
  const eventId = eventData.id
  requestedEventIds.value.push(eventId)

  // scroll to the top of the component
  if (dateRequestSuccessRef.value) {
    dateRequestSuccessRef.value.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }
}

const eventsNotRequested = computed(
  () =>
    similarEvents.value
      ?.filter(
        (event: DateRequestSimilarEvent) =>
          !requestedEventIds.value.includes(event.id)
      )
      .slice(0, 6) ?? []
)

const eventsWithBookableDate = computed(
  () =>
    eventsNotRequested.value?.filter(
      (event: DateRequestSimilarEvent) => event.bookable_date
    ) ?? []
)

const eventsWithoutBookableDate = computed(
  () =>
    eventsNotRequested.value?.filter(
      (event: DateRequestSimilarEvent) => !event.bookable_date
    ) ?? []
)

const eventsRequested = computed(
  () =>
    similarEvents.value?.filter((event: DateRequestSimilarEvent) =>
      requestedEventIds.value.includes(event.id)
    ) ?? []
)
</script>

<style lang="postcss" scoped>
.complete-alert {
  @apply p-4 flex gap-2 items-center justify-center bg-success-light rounded-lg text-sm;
}

/* Style ProductCard title to be smaller inside DateRequestSuccess */
:deep(.product-card__title) {
  @apply text-xs md:text-sm;
}
</style>
